import './PunchupInlineEditor.css';

import { FC, ReactNode, useEffect, useState } from 'react';

import { PunchupAddButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { useOptionalAccordionSection } from 'features/common/components/Punchup/primitives/layout/PunchupAccordion';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ViewHelpers {
	startAdd: () => void;
	startEdit: (id: string) => void;
}

export interface EditHelpers {
	/** null = creating a new entity; string = editing the entity with this id */
	editingId: string | null;
	done: () => void;
}

interface PunchupInlineEditorProps {
	/**
	 * Renders the list/view state. Receives helpers to transition to edit mode.
	 * The Add button is rendered automatically below by this component —
	 * do NOT include it inside renderView.
	 */
	renderView: (helpers: ViewHelpers) => ReactNode;
	/**
	 * Renders the add/edit form state. Receives the editingId (null = create)
	 * and a done() callback to return to view state.
	 * The form is responsible for its own Cancel/Save buttons (use entityEditActions CSS class).
	 */
	renderEdit: (helpers: EditHelpers) => ReactNode;
	/** Label for the bottom-right Add button in view mode. */
	addLabel: string;
	/** Whether the Add button is disabled (e.g. max tags reached). */
	addDisabled?: boolean;
	/**
	 * Native HTML title shown on hover when the Add button is disabled — explains *why* the
	 * affordance is unavailable. Has no effect when `addDisabled` is false.
	 */
	addDisabledTitle?: string;
	/**
	 * Extra action buttons rendered to the left of the Add button in view mode.
	 * Use PunchupActionButton for each extra action.
	 */
	extraViewActions?: ReactNode;
	/**
	 * Extra action buttons rendered to the right of the Add button in view mode.
	 * Use PunchupActionButton for each extra action.
	 */
	appendViewActions?: ReactNode;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * PunchupInlineEditor
 *
 * Manages the view/edit state transition inside a PunchupAccordionSection.
 * Renders the view state (entity list) with a bottom-right Add button,
 * or the edit state (inline form) replacing the full content area.
 *
 * Behaviour when the enclosing accordion section closes:
 * - State resets to 'view' so the section always opens in list mode, never mid-edit.
 *
 * On done() (cancel or save from a form):
 * - Scrolls the accordion section header into view so the user can see the result.
 *
 * Parent: AccordionContentPadded — already applies flex column + 24px gap.
 *
 * CSS classes are in PunchupInlineEditor.css, which shares the same class names
 * as downstroke/apps/web/styles/punchup-entity-section.css.
 */
export const PunchupInlineEditor: FC<PunchupInlineEditorProps> = ({
	renderView,
	renderEdit,
	addLabel,
	addDisabled = false,
	addDisabledTitle,
	extraViewActions,
	appendViewActions,
}) => {
	const [mode, setMode] = useState<'view' | 'edit'>('view');
	const [editingId, setEditingId] = useState<string | null>(null);

	// Read optional section context — null when rendered outside an accordion (tests, etc.)
	const section = useOptionalAccordionSection();

	// Reset to view state when the enclosing accordion section collapses.
	// This ensures the section always opens in list mode, never mid-edit.
	useEffect(() => {
		if (section && !section.isOpen) {
			setMode('view');
			setEditingId(null);
		}
	}, [section?.isOpen]);

	const startAdd = () => {
		setEditingId(null);
		setMode('edit');
	};

	const startEdit = (id: string) => {
		setEditingId(id);
		setMode('edit');
	};

	const done = () => {
		setEditingId(null);
		setMode('view');
		// Scroll the accordion header into view so the user sees the updated list.
		section?.scrollToSection('start');
	};

	if (mode === 'edit') {
		const isSaving = section?.isSaving ?? false;
		// Wrapper is unconditional in edit mode; only the overlay toggles with `isSaving`.
		// Do NOT collapse the non-saving branch back to a Fragment: a Fragment<->div parent
		// swap remounts the form (resets isSubmitting, re-runs the edit-query reset()).
		// Full narrative + regression test: see PunchupInlineEditor/CLAUDE.md § "Save overlay".
		return (
			<div className="entityEditSavingWrapper">
				{renderEdit({ editingId, done })}
				{isSaving && (
					<div className="entityEditSavingOverlay">
						<PunchupSpinner size={32} />
					</div>
				)}
			</div>
		);
	}

	return (
		<>
			{renderView({ startAdd, startEdit })}
			<div className="entityViewActions">
				{extraViewActions}
				<PunchupAddButton
					type="button"
					onClick={startAdd}
					disabled={addDisabled}
					title={addDisabled ? addDisabledTitle : undefined}
				>
					{addLabel}
				</PunchupAddButton>
				{appendViewActions}
			</div>
		</>
	);
};
